import React, { useEffect, useRef, useState } from 'react';
import classes from './EditReplicationFlow.module.scss';
import { CDCPipeline } from 'new/elements/flows/CDCPipeline';
import Typography from 'new/elements/Typography';
import { Tabs } from 'new/elements/Tabs';
import { TabProps } from 'new/elements/Tabs/Tabs';
import { useDispatch, useSelector } from 'react-redux';
import { loadFlowMetrics, loadPipeline } from 'js/actions';
import { useHistory } from 'react-router-dom';
import { ThunkDispatch } from 'redux-thunk';
import { Action } from 'redux';
import { FlowLogs } from 'new/components/flows/types/replication/EditReplicationFlow/FlowLogs';
import { Config } from 'new/components/flows/types/replication/EditReplicationFlow/Config';
import { useDistance } from 'new/hooks/useDistance';
import { Stack } from 'new/elements/Stack';
import DataFlowDetailActions from 'js/components/data_flows/DataFlowDetailActionsContainer';
import { RESOURCE_TYPE_SINK } from 'js/constants/resourceTypes';
import { ROUTE_CONNECT } from 'new/constants/pathnames';
import { ROUTE_PIPELINES } from 'js/constants/pathnames';
import ModalCloneFlowContainer from 'js/components/pipelines/ModalCloneFlowContainer';
import { DetailView } from 'new/elements/DetailView';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import { GET_PIPELINES_REQUEST } from 'js/constants/actionTypes';
import { FLOW_METRICS_INTERVAL_LIFETIME } from 'js/constants/metricsConstants';
import { CenteredLoader } from 'new/elements/CenteredLoader';
import { useReplicationFlow } from 'new/components/flows/types/replication/EditReplicationFlow/hooks/useReplicationFlow';
import { ScreenConfig } from 'new/routes/RouterManager';
import FlowDetailViewContainer from 'js/components/data_flows/cards/FlowDetailViewContainer';
import pipelines from 'js/components/pipelines/Pipelines';
import useFlowParams from 'new/components/flows/useFlowParams';

interface EditReplicationFlowProps {}

const tabs = [
  {
    label: 'Flow activity',
    value: 'flow',
  },
  {
    label: 'Source config',
    value: 'source',
  },
  {
    label: 'Destination config',
    value: 'sink',
  },
] as TabProps[];

const Bottom = ({ resources }) => {
  const bottomRef = useRef();
  const bottomDistance = useDistance(bottomRef);
  const [tab, setTab] = useState('flow');
  if (!resources?.source) return null;
  let bottomTabs = [...tabs];
  if (!resources.nexset) {
    bottomTabs = [tabs[0]];
  } else if (!resources.destination) {
    bottomTabs.splice(2, 1);
  }

  const onChangeTab = (e, value: string) => {
    setTab(value);
  };

  return (
    <div className={classes.bottom}>
      <div ref={bottomRef} className={classes.tabsContainer}>
        <Tabs onChange={onChangeTab} tabs={bottomTabs} value={tab} />
      </div>
      {tab === 'flow' ? (
        <FlowLogs source={resources.source} />
      ) : (
        <Config
          config={
            tab === 'source' ? resources.source?.source_config : resources.destination?.sink_config
          }
          height={bottomDistance}
        />
      )}
    </div>
  );
};

export const EditReplicationFlow = (props: EditReplicationFlowProps) => {
  const dispatch = useDispatch<ThunkDispatch<any, any, Action>>();
  const history = useHistory();
  const [cloneFlow, setCloneFlow] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const flowSelectedId = useSelector((state: any) => state.pipelines.detailView.id);
  const { id } = useFlowParams();

  const resources = useReplicationFlow(id);

  const loading = useIsLoading(GET_PIPELINES_REQUEST);

  const getFlow = () => {
    dispatch(loadPipeline(id));
  };

  useEffect(() => {
    getFlow();
  }, [id]);

  useEffect(() => {
    if (resources?.source?.origin_node_id) {
      dispatch(
        loadFlowMetrics(resources.source.origin_node_id, {
          interval: FLOW_METRICS_INTERVAL_LIFETIME,
        }),
      );
    }
  }, [resources?.source?.origin_node_id]);

  const onClickResource = (type) => {
    if (type === RESOURCE_TYPE_SINK && !resources.destination) {
      history.push(
        `${ROUTE_CONNECT}${ROUTE_PIPELINES}/replication/sink/new?flowId=${resources.source.id}`,
      );
    }
  };

  const toggleCloneFlowModal = (flow) => {
    setCloneFlow(flow);
  };

  const onCloseDetail = () => {
    setDetailData({});
  };

  const onOpenDetail = (data, meta) => {
    setDetailData({ data, meta });
  };

  if (loading) {
    return <CenteredLoader />;
  }

  return (
    <div className={classes.container}>
      <div className={classes.top}>
        <Typography>Replication</Typography>
        <Stack direction="row" gap="16px" alignItems="center">
          <DataFlowDetailActions
            data={resources.flow}
            toggleCloneFlowModal={toggleCloneFlowModal}
          />
        </Stack>
      </div>
      <div className={classes.content}>
        <CDCPipeline
          onOpenDetailClicked={onOpenDetail}
          onClickResource={onClickResource}
          {...resources}
        />
      </div>
      <Bottom resources={resources} />
      {cloneFlow ? (
        <ModalCloneFlowContainer data={cloneFlow} toggleCloneFlowModal={toggleCloneFlowModal} />
      ) : null}
      {flowSelectedId ? (
        <FlowDetailViewContainer toggleCloneFlowModal={toggleCloneFlowModal} />
      ) : null}
      <DetailView
        data={detailData?.data}
        meta={detailData?.meta}
        showTransform={false}
        onClose={onCloseDetail}
      />
    </div>
  );
};

EditReplicationFlow.screenConfig = {
  screenName: 'EditReplicationFlow',
  domain: 'main',
  path: [`${ROUTE_CONNECT}/${ROUTE_PIPELINES}/replication/:id/edit`],
  pageTitle: 'Edit Replication Flow',
  header: {
    title: 'Edit Replication Flow',
  },
} as ScreenConfig<any>;
